import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TreeDataTable, Badge, PageHeader, PageLoading, ErrorBanner, type TreeNode, type ColumnDef } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { useApi } from '@/hooks/useApi';
import { orgHierarchyApi } from '@/services/org-hierarchy-api';
import { schemaRelationshipApi } from '@/services/schema-relationship-api';
import type {
  IOrgHierarchy,
  IOrgHierarchyProduct,
  IOrgHierarchyRepository,
  IOrgHierarchyTeam,
} from '@skystone/shared';

// Unified node type for the tree

interface IOrgNode {
  id: string;
  name: string;
  type: 'group' | 'product' | 'team' | 'repository';
  fabricSynced: boolean;
  archived: boolean;
  fabricTeamId?: string | null;
  fabricRepositoryId?: string | null;
  childCount?: number;
}

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

function teamToNode(team: IOrgHierarchyTeam): TreeNode<IOrgNode> {
  return {
    data: {
      id: team.id,
      name: team.name,
      type: 'team',
      fabricSynced: team.fabricSynced,
      archived: team.archived,
      fabricTeamId: team.fabricTeamId,
      childCount: 0,
    },
  };
}

function repositoryToNode(repository: IOrgHierarchyRepository): TreeNode<IOrgNode> {
  return {
    data: {
      id: repository.id,
      name: repository.name,
      type: 'repository',
      fabricSynced: repository.fabricSynced,
      archived: repository.archived,
      fabricRepositoryId: repository.fabricRepositoryId,
      childCount: 0,
    },
  };
}

function productToNode(product: IOrgHierarchyProduct): TreeNode<IOrgNode> {
  const repositoryChildren = (product.repositories ?? []).map(repositoryToNode);
  const teamChildren = product.teams.map(teamToNode);
  const children = [...repositoryChildren, ...teamChildren];

  return {
    data: {
      id: product.id,
      name: product.name,
      type: 'product',
      fabricSynced: product.fabricSynced,
      archived: product.archived,
      childCount: children.length,
    },
    children,
  };
}

function buildTreeNodes(hierarchy: IOrgHierarchy): TreeNode<IOrgNode>[] {
  const products = hierarchy.products ?? [];
  const unassignedTeams = hierarchy.unassignedTeams ?? [];
  const unassignedRepositories = hierarchy.unassignedRepositories ?? [];

  const nodes: TreeNode<IOrgNode>[] = products.map(productToNode);

  if (unassignedTeams.length > 0) {
    nodes.push({
      data: {
        id: '__unassigned-teams',
        name: 'Unassigned Teams',
        type: 'group',
        fabricSynced: false,
        archived: false,
        childCount: unassignedTeams.length,
      },
      children: unassignedTeams.map(teamToNode),
    });
  }

  if (unassignedRepositories.length > 0) {
    nodes.push({
      data: {
        id: '__unassigned-repositories',
        name: 'Unassigned Repositories',
        type: 'group',
        fabricSynced: false,
        archived: false,
        childCount: unassignedRepositories.length,
      },
      children: unassignedRepositories.map(repositoryToNode),
    });
  }

  return nodes;
}

function collectExpandedKeys(hierarchy: IOrgHierarchy): Set<string> {
  const products = hierarchy.products ?? [];
  const unassignedTeams = hierarchy.unassignedTeams ?? [];
  const unassignedRepositories = hierarchy.unassignedRepositories ?? [];
  const keys = new Set<string>();
  for (const product of products) {
    keys.add(product.id);
  }
  if (unassignedTeams.length > 0) {
    keys.add('__unassigned-teams');
  }
  if (unassignedRepositories.length > 0) {
    keys.add('__unassigned-repositories');
  }
  return keys;
}

// ---------------------------------------------------------------------------
// Link helper
// ---------------------------------------------------------------------------

function detailHref(node: IOrgNode): string {
  switch (node.type) {
    case 'group':
      return '#';
    case 'product':
      return '#';
    case 'team':
      return `/admin/teams/${node.id}`;
    case 'repository':
      return `/admin/repositories/${encodeURIComponent(node.id)}`;
  }
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const TYPE_BADGE_VARIANT: Record<IOrgNode['type'], 'default' | 'secondary'> = {
  group: 'default',
  product: 'secondary',
  team: 'secondary',
  repository: 'secondary',
};

const TYPE_LABELS: Record<IOrgNode['type'], string> = {
  group: 'Group',
  product: 'Product',
  team: 'Team',
  repository: 'Repository',
};

function pathStatusVariant(status: string): 'default' | 'warning' | 'secondary' {
  if (status === 'complete') {
    return 'default';
  }

  if (status === 'partial') {
    return 'warning';
  }

  return 'secondary';
}

const columns: ColumnDef<IOrgNode>[] = [
  {
    id: 'name',
    header: 'Name',
    accessor: 'name',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter name...' },
    cell: (_value, row) => {
      // Don't link synthetic nodes
      if (row.id.startsWith('__')) {
        return <span className="font-medium text-muted-foreground">{row.name}</span>;
      }
      return (
        <Link
          to={detailHref(row)}
          className="text-primary hover:underline font-medium"
        >
          {row.name}
        </Link>
      );
    },
  },
  {
    id: 'type',
    header: 'Type',
    accessor: 'type',
    filter: {
      type: 'select',
      placeholder: 'All types',
      options: [
        { label: 'Group', value: 'group' },
        { label: 'Product', value: 'product' },
        { label: 'Repository', value: 'repository' },
        { label: 'Team', value: 'team' },
      ],
    },
    cell: (_value, row) => (
      <Badge variant={TYPE_BADGE_VARIANT[row.type]}>
        {TYPE_LABELS[row.type]}
      </Badge>
    ),
  },
  {
    id: 'fabricSynced',
    header: 'Fabric',
    accessor: 'fabricSynced',
    cell: (value) =>
      value ? (
        <Badge variant="success">Synced</Badge>
      ) : (
        <Badge variant="secondary">Manual</Badge>
      ),
  },
  {
    id: 'archived',
    header: 'Status',
    accessor: 'archived',
    cell: (value) =>
      value ? (
        <Badge variant="warning">Archived</Badge>
      ) : (
        <Badge variant="success">Active</Badge>
      ),
  },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function OrgHierarchyPage() {
  const [showArchived, setShowArchived] = useState(false);

  const { data: hierarchy, loading, error } = useApi(
    () => orgHierarchyApi.getAll(showArchived),
    [showArchived],
  );

  const { data: relationshipSnapshot } = useApi(
    () => schemaRelationshipApi.getLatest(),
    [],
  );

  const treeNodes = useMemo(
    () => (hierarchy ? buildTreeNodes(hierarchy) : []),
    [hierarchy],
  );

  const defaultExpandedKeys = useMemo(
    () => (hierarchy ? collectExpandedKeys(hierarchy) : new Set<string>()),
    [hierarchy],
  );

  const summary = useMemo(() => {
    const products = hierarchy?.products ?? [];
    const productCount = products.length;
    const productTeamCount = products.reduce((acc, product) => acc + product.teams.length, 0);
    const productRepositoryCount = products.reduce((acc, product) => acc + (product.repositories?.length ?? 0), 0);
    const unassignedTeamCount = hierarchy?.unassignedTeams.length ?? 0;
    const unassignedRepositoryCount = hierarchy?.unassignedRepositories?.length ?? 0;

    return {
      productCount,
      teamCount: productTeamCount + unassignedTeamCount,
      repositoryCount: productRepositoryCount + unassignedRepositoryCount,
      unassignedTeamCount,
      unassignedRepositoryCount,
    };
  }, [hierarchy]);

  const pathStatuses = useMemo(() => {
    const map = new Map<string, string>();
    for (const path of relationshipSnapshot?.graph.paths ?? []) {
      map.set(path.name, path.status);
    }
    return {
      unified: map.get('End-to-End Delivery Ownership') ?? 'unknown',
      org: map.get('Org Hierarchy') ?? 'unknown',
      delivery: map.get('Delivery Chain') ?? 'unknown',
    };
  }, [relationshipSnapshot]);

  if (loading && !hierarchy) return <PageLoading />;

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Org Hierarchy"
        subtitle="Product -> Repository + Team hierarchy with unified connection status."
      />

      <ErrorBanner message={error} />

      <div className="rounded-md border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge variant="secondary">Products: {summary.productCount}</Badge>
          <Badge variant="secondary">Teams: {summary.teamCount}</Badge>
          <Badge variant="secondary">Repositories: {summary.repositoryCount}</Badge>
          <Badge variant={summary.unassignedTeamCount > 0 ? 'warning' : 'success'}>
            Unassigned Teams: {summary.unassignedTeamCount}
          </Badge>
          <Badge variant={summary.unassignedRepositoryCount > 0 ? 'warning' : 'success'}>
            Unassigned Repositories: {summary.unassignedRepositoryCount}
          </Badge>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Path Health:</span>
          <Badge variant={pathStatusVariant(pathStatuses.org)}>Org: {pathStatuses.org}</Badge>
          <Badge variant={pathStatusVariant(pathStatuses.delivery)}>Delivery: {pathStatuses.delivery}</Badge>
          <Badge variant={pathStatusVariant(pathStatuses.unified)}>Unified: {pathStatuses.unified}</Badge>
          <Link to="/admin/schema-relationships" className="text-primary hover:underline font-medium">
            Open Schema Relationships
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label
          htmlFor="show-archived"
          className="text-sm text-muted-foreground cursor-pointer select-none"
        >
          Show archived
        </label>
        <input
          id="show-archived"
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
        />
      </div>

      <TreeDataTable
        nodes={treeNodes}
        columns={columns}
        keyExtractor={(row) => row.id}
        defaultExpandedKeys={defaultExpandedKeys}
        enablePagination
        enableFilters
        defaultPageSize={50}
        emptyMessage="No hierarchy data found."
      />
    </PageContainer>
  );
}
